import { useSyncExternalStore } from 'react';
import { direction, languages, matchLocale, negotiateLocale, translate, plural as formatPlural, loadLanguage, type AppLocale } from '../../../../packages/localization/src/browser';
export { languages, direction };
const listeners = new Set<()=>void>();
function storedPreference() { try { return matchLocale(localStorage.getItem('promise.language')) } catch { return null } }
let preference: AppLocale | null = storedPreference();
let revision=0, pending=false, choiceVersion=0;
// An explicit link language is a presentation choice for this navigation,
// never a write to the account preference. Policy/confirmation links can thus
// remain readable when opened in another browser or from the native app.
let navigationLocale = matchLocale(new URLSearchParams(location.search).get('lang'));
let locale = navigationLocale || preference || negotiateLocale(navigator.languages.join(','));
function apply() {
  document.documentElement.lang=locale;
  document.documentElement.dir=direction(locale);
  // The non-secret preference also selects the language of server-rendered
  // confirmations. It never confers authentication or card access.
  document.cookie=`promise-language=${locale}; Path=/; Max-Age=31536000; SameSite=Lax${location.protocol==='https:' ? '; Secure' : ''}`;
  revision++;
  for (const listener of listeners) listener();
}
apply();
export function currentLocale() { return locale; }
export function languagePreference() { return preference; }
let generation=0;
let startupError="";
export function initialLanguageError(){return startupError;}
export async function initializeLanguage(){
  try { await loadLanguage(locale); }
  catch { locale="en";startupError=t("The language could not load. Refresh this page and try again. Saved drafts are kept."); }
  apply();
}
export async function setLanguage(value: AppLocale | null, source:'user'|'account'='user') {
  const request=++generation,next=matchLocale(value),resolved=(source==='account' ? navigationLocale : null) || next || negotiateLocale(navigator.languages.join(','));
  try { await loadLanguage(resolved); } catch { throw new Error(t("The language could not load. Refresh this page and try again. Saved drafts are kept.")); }
  if(request!==generation)return;
  if(source==='user') {
    navigationLocale=null;
    const url=new URL(location.href);
    if(url.searchParams.has('lang')) { url.searchParams.delete('lang');history.replaceState(history.state,'',url); }
  }
  preference=next;
  try { if(preference) localStorage.setItem('promise.language',preference); else localStorage.removeItem('promise.language'); } catch { /* Memory-only language choice still works. */ }
  locale=resolved;
  apply();
}
export function useLanguage() {
  useSyncExternalStore(listener=>{listeners.add(listener);return ()=>listeners.delete(listener)},()=>revision);
  return {locale,preference,pending};
}
export function t(key: string,values?:Record<string,string|number>) { return translate(locale,key,values); }
export function countText(key: string,count:number) { return formatPlural(locale,key,count); }
export function formatNumber(value:number) { return new Intl.NumberFormat(locale).format(value); }

export function languagePending() { return pending; }
export function languageChoiceVersion() { return choiceVersion; }
export function setLanguagePending(value:boolean) {
  if(value && !pending)choiceVersion++;
  pending=value;
  apply();
}

export function themeName(theme:string) { return t(({sage:"Sage",peach:"Peach",lavender:"Lavender",butter:"Butter"} as Record<string,string>)[theme] || theme); }
